import {useMemo} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {
    AI_APPENDIX_ENTRIES,
    AI_INDEX_ENTRIES,
    type AiIndexEntry,
} from '@/pages/components/audit-program/ai-section/constants';
import {
    isAiIndexEntryVisible,
    type DeriveActiveIndexResult,
} from '@/pages/components/audit-program/ai-section/deriveActiveIndex';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';
import type {SectionIndexRow} from '@/pages/components/audit-program/SectionIndexView';

export type AiIndexViewProps = {
    header: AuditProgramSectionVO;
    /** A FINAL COMPLETION TOC (lower half of Excel Ai). */
    sectionRows: SectionIndexRow[];
    sectionLetter?: string;
    sectionTitle?: string;
    /** BS/PL-derived active letters/appendices — inactive rows render with strikethrough. */
    activeIndex?: DeriveActiveIndexResult | null;
    onNavigate?: (pageCode: string) => void;
};

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.75rem',
    py: 0.75,
    px: 1,
};
const bodySx = {
    ...cellBorder,
    py: 0.55,
    px: 1,
    verticalAlign: 'top' as const,
};

function strikeSx(struck: boolean) {
    return struck
        ? {textDecoration: 'line-through', color: 'text.disabled'}
        : undefined;
}

/**
 * Excel Ai sheet: AUDIT FILE INDEX (A–Z + Appendices) + A FINAL COMPLETION TOC.
 */
export default function AiIndexView({
    header,
    sectionRows,
    sectionLetter = 'A',
    sectionTitle = 'FINAL COMPLETION',
    activeIndex = null,
    onNavigate,
}: AiIndexViewProps) {
    const fileRows = useMemo(() => {
        const mark = (entry: AiIndexEntry) => ({
            entry,
            struck: activeIndex
                ? !isAiIndexEntryVisible(entry, activeIndex)
                : Boolean(entry.neverVisible),
        });
        return {
            letters: AI_INDEX_ENTRIES.map(mark),
            appendices: AI_APPENDIX_ENTRIES.map(mark),
        };
    }, [activeIndex]);

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader header={header} variant="a1" refCode="Ai"/>

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', mt: 1, mb: 1.5, letterSpacing: 0.5}}
            >
                AUDIT FILE INDEX
            </Typography>

            <TableContainer sx={{mb: 3}}>
                <Table size="small" sx={{'& td, & th': cellBorder}}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{...headSx, width: 72}}>Ref</TableCell>
                            <TableCell sx={headSx}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fileRows.letters.map(({entry, struck}) => (
                            <TableRow key={`letter-${entry.code}`}>
                                <TableCell sx={{...bodySx, fontWeight: 600, ...strikeSx(struck)}}>
                                    {entry.code}
                                </TableCell>
                                <TableCell sx={{...bodySx, ...strikeSx(struck)}}>
                                    {entry.title}
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell colSpan={2} sx={{...bodySx, fontWeight: 700, pt: 1.5}}>
                                APPENDICES
                            </TableCell>
                        </TableRow>
                        {fileRows.appendices.map(({entry, struck}) => (
                            <TableRow key={`appendix-${entry.code}`}>
                                <TableCell sx={{...bodySx, fontWeight: 600, ...strikeSx(struck)}}>
                                    {entry.code}
                                </TableCell>
                                <TableCell sx={{...bodySx, ...strikeSx(struck)}}>
                                    {entry.title}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', mt: 1, mb: 2, letterSpacing: 0.5}}
            >
                {sectionLetter}&nbsp;&nbsp;&nbsp;{sectionTitle}
            </Typography>

            <TableContainer>
                <Table size="small" sx={{'& td, & th': cellBorder}}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{...headSx, width: 72}}>Ref</TableCell>
                            <TableCell sx={headSx}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sectionRows.map((row) => {
                            const clickable = Boolean(row.pageCode && onNavigate);
                            const isSection = row.kind === 'section';
                            return (
                                <TableRow
                                    key={row.id}
                                    hover={clickable}
                                    onClick={clickable ? () => onNavigate?.(row.pageCode!) : undefined}
                                    sx={clickable ? {cursor: 'pointer'} : undefined}
                                >
                                    <TableCell
                                        sx={{
                                            ...bodySx,
                                            fontWeight: isSection || clickable ? 600 : 400,
                                            color: clickable ? 'primary.main' : 'text.primary',
                                        }}
                                    >
                                        {row.num}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            ...bodySx,
                                            fontWeight: isSection ? 600 : 400,
                                            color: clickable ? 'primary.main' : 'text.primary',
                                        }}
                                    >
                                        {row.title}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
